// 📱 iOS Compatibility Utilities
// Narzędzia do zapewnienia kompatybilności z iOS Safari

/**
 * Sprawdza czy urządzenie to iOS
 */
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

/**
 * Sprawdza czy to Safari na iOS
 */
export const isIOSSafari = () => {
  const ua = navigator.userAgent;
  return isIOS() && /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|mercury/.test(ua);
};

/**
 * Sprawdza czy aplikacja jest uruchomiona w trybie standalone (PWA)
 */
export const isStandalone = () => {
  return window.navigator.standalone === true || 
         window.matchMedia('(display-mode: standalone)').matches;
};

/**
 * Naprawia problemy z viewport na iOS
 */
export const fixIOSViewport = () => {
  if (!isIOS()) return;
  
  // Napraw problem z viewport height na iOS
  const setViewportHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };
  
  setViewportHeight();
  window.addEventListener('resize', setViewportHeight);
  window.addEventListener('orientationchange', () => {
    setTimeout(setViewportHeight, 100);
  });
};

/**
 * Naprawia problemy z touch events na iOS
 */
export const fixIOSTouchEvents = () => {
  if (!isIOS()) return;
  
  // Zapobiega domyślnemu zachowaniu touch events
  document.addEventListener('touchstart', (e) => {
    // Pozwól na normalne działanie dla elementów input
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      return;
    }
  }, { passive: true });
  
  // Napraw problem z double-tap zoom
  let lastTouchEnd = 0;
  document.addEventListener('touchend', (e) => {
    const now = (new Date()).getTime();
    if (now - lastTouchEnd <= 300) {
      e.preventDefault();
    }
    lastTouchEnd = now;
  }, false);
};

/**
 * Naprawia problemy z audio na iOS
 */
export const fixIOSAudio = () => {
  if (!isIOS()) return;
  
  // iOS wymaga interakcji użytkownika przed odtworzeniem audio
  const unlockAudio = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const buffer = audioContext.createBuffer(1, 1, 22050);
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(audioContext.destination);
    source.start(0);
    
    document.removeEventListener('touchstart', unlockAudio);
    document.removeEventListener('touchend', unlockAudio);
  };
  
  document.addEventListener('touchstart', unlockAudio);
  document.addEventListener('touchend', unlockAudio);
};

/**
 * Naprawia problemy z vibration API na iOS
 */
export const fixIOSVibration = () => {
  if (!isIOS()) return;
  
  // iOS nie wspiera Vibration API, ale możemy symulować feedback
  if (!navigator.vibrate) {
    navigator.vibrate = (pattern) => {
      console.log('iOS: Vibration simulated', pattern);
      // Możemy dodać wizualny feedback zamiast wibracji
      return true;
    };
  }
};

/**
 * Naprawia problemy z device motion na iOS
 */
export const fixIOSDeviceMotion = () => {
  if (!isIOS()) return;
  
  // iOS 13+ wymaga pozwolenia na dostęp do device motion
  const requestMotionPermission = async () => {
    if (typeof DeviceMotionEvent !== 'undefined' && typeof DeviceMotionEvent.requestPermission === 'function') {
      try {
        const permission = await DeviceMotionEvent.requestPermission();
        return permission === 'granted';
      } catch (error) {
        console.log('iOS: Device motion permission denied', error);
        return false;
      }
    }
    return true;
  };
  
  return requestMotionPermission;
};

/**
 * Naprawia problemy z localStorage na iOS w trybie prywatnym
 */
export const fixIOSLocalStorage = () => {
  if (!isIOS()) return;
  
  try {
    localStorage.setItem('test', 'test');
    localStorage.removeItem('test');
  } catch (e) {
    // Fallback dla trybu prywatnego iOS Safari
    const storage = {};
    Storage.prototype.setItem = function(key, value) {
      storage[key] = value;
    };
    Storage.prototype.getItem = function(key) {
      return storage[key] || null;
    };
    Storage.prototype.removeItem = function(key) {
      delete storage[key];
    };
    Storage.prototype.clear = function() {
      for (let key in storage) {
        delete storage[key];
      }
    };
  }
};

/**
 * Naprawia problemy z CSS na iOS
 */
export const fixIOSCSS = () => {
  if (!isIOS()) return;
  
  // Dodaj iOS-specific CSS
  const style = document.createElement('style');
  style.textContent = `
    /* iOS Safari fixes */
    * {
      -webkit-tap-highlight-color: transparent;
      -webkit-touch-callout: none;
    }
    
    body {
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: none;
    }
    
    input, textarea, select {
      -webkit-appearance: none;
      border-radius: 0;
    }
    
    /* Fix for iOS viewport units */
    .ios-vh-fix {
      height: 100vh;
      height: calc(var(--vh, 1vh) * 100);
    }
    
    /* Fix for iOS safe area */
    .ios-safe-area {
      padding-top: env(safe-area-inset-top);
      padding-bottom: env(safe-area-inset-bottom);
      padding-left: env(safe-area-inset-left);
      padding-right: env(safe-area-inset-right);
    }
    
    /* Fix for iOS button styling */
    button {
      -webkit-appearance: none;
      border-radius: 0;
    }
    
    /* Fix for iOS scroll momentum */
    .ios-scroll {
      -webkit-overflow-scrolling: touch;
      overflow-scrolling: touch;
    }
  `;
  document.head.appendChild(style);
};

/**
 * Naprawia problemy z Service Worker na iOS
 */
export const fixIOSServiceWorker = () => {
  if (!isIOS()) return;
  
  // iOS Safari ma ograniczone wsparcie dla Service Workers
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none'
    }).then((registration) => {
      console.log('iOS: Service Worker registered', registration);
      
      // Sprawdź aktualizacje częściej na iOS
      setInterval(() => {
        registration.update();
      }, 60000); // Co minutę
      
    }).catch((error) => {
      console.log('iOS: Service Worker registration failed', error);
    });
  }
};

/**
 * Inicjalizuje wszystkie poprawki iOS
 */
export const initIOSCompatibility = () => {
  if (!isIOS()) {
    console.log('Not iOS device, skipping iOS compatibility fixes');
    return;
  }
  
  console.log('iOS device detected, applying compatibility fixes...');
  
  fixIOSViewport();
  fixIOSTouchEvents();
  fixIOSAudio();
  fixIOSVibration();
  fixIOSLocalStorage();
  fixIOSCSS();
  fixIOSServiceWorker();
  
  console.log('iOS compatibility fixes applied');
};

/**
 * Sprawdza czy funkcja jest dostępna na iOS
 */
export const checkIOSFeatureSupport = () => {
  const features = {
    vibration: 'vibrate' in navigator,
    deviceMotion: 'DeviceMotionEvent' in window,
    deviceOrientation: 'DeviceOrientationEvent' in window,
    geolocation: 'geolocation' in navigator,
    serviceWorker: 'serviceWorker' in navigator,
    pushNotifications: 'PushManager' in window,
    webShare: 'share' in navigator,
    fullscreen: 'requestFullscreen' in document.documentElement,
    webGL: !!window.WebGLRenderingContext,
    webAudio: !!(window.AudioContext || window.webkitAudioContext),
    localStorage: (() => {
      try {
        localStorage.setItem('test', 'test');
        localStorage.removeItem('test');
        return true;
      } catch (e) {
        return false;
      }
    })()
  };
  
  console.log('iOS Feature Support:', features);
  return features;
};

/**
 * Pokazuje instrukcje instalacji PWA na iOS
 */
export const showIOSInstallPrompt = () => {
  if (!isIOSSafari() || isStandalone()) return false;
  
  const prompt = document.createElement('div');
  prompt.style.cssText = `
    position: fixed;
    bottom: 20px;
    left: 20px;
    right: 20px;
    background: #007AFF;
    color: white;
    padding: 15px;
    border-radius: 10px;
    font-family: -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 14px;
    z-index: 10000;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  `;
  
  prompt.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: space-between;">
      <div>
        <strong>📱 Zainstaluj aplikację</strong><br>
        Kliknij <span style="border: 1px solid white; padding: 2px 6px; border-radius: 3px;">⬆️</span> i wybierz "Dodaj do ekranu głównego"
      </div>
      <button onclick="this.parentElement.parentElement.remove()" style="background: none; border: none; color: white; font-size: 18px; cursor: pointer;">✕</button>
    </div>
  `;
  
  document.body.appendChild(prompt);
  
  // Usuń prompt po 10 sekundach
  setTimeout(() => {
    if (prompt.parentElement) {
      prompt.remove();
    }
  }, 10000);
  
  return true;
};

export default {
  isIOS,
  isIOSSafari,
  isStandalone,
  initIOSCompatibility,
  checkIOSFeatureSupport,
  showIOSInstallPrompt,
  fixIOSViewport,
  fixIOSTouchEvents,
  fixIOSAudio,
  fixIOSVibration,
  fixIOSDeviceMotion,
  fixIOSLocalStorage,
  fixIOSCSS,
  fixIOSServiceWorker
};

